import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, cx, PageSlot } from '@kanos/ui';
import { ArrowUp } from 'lucide-react';
import { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PageNamesCard } from './card.js';
import { usePageTitle } from './page-state.js';
/**
 * Follows the head of a page out of view (after Corly): whether its title went above the top
 * of the scroll area, and the card one is in then - the last card heading above that edge.
 */
function useGoneHead() {
    const ref = useRef(null);
    const [gone, setGone] = useState(false);
    const [card, setCard] = useState(null);
    useEffect(() => {
        const title = ref.current;
        const main = title?.closest('main');
        if (!title || !main)
            return;
        const observer = new IntersectionObserver(([entry]) => {
            if (!entry)
                return;
            // Only upwards: a title below the edge is not yet a reason to show it at the top.
            setGone(!entry.isIntersecting && entry.boundingClientRect.top < (entry.rootBounds?.top ?? 0));
        }, { root: main });
        observer.observe(title);
        return () => observer.disconnect();
    }, []);
    useEffect(() => {
        const main = ref.current?.closest('main');
        if (!gone || !main) {
            setCard(null);
            return;
        }
        let frame = 0;
        const read = () => {
            frame = 0;
            const top = main.getBoundingClientRect().top;
            const passed = [...main.querySelectorAll('section h2')].filter((h) => h.getBoundingClientRect().top < top + 8 && !h.classList.contains('sr-only'));
            setCard(passed.at(-1)?.textContent ?? null);
        };
        const onScroll = () => {
            frame ||= requestAnimationFrame(read);
        };
        read();
        main.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            main.removeEventListener('scroll', onScroll);
            cancelAnimationFrame(frame);
        };
    }, [gone]);
    return { ref, gone, card };
}
/**
 * A page of an app (after Corly's `AfxPage` and `AfxDetailPage`): its head - the way back, the
 * title with its pills, a line under it, the key figures, the actions on the right, a hairline
 * under all of it - then its content. Once the title scrolls away, a copy of it goes up into the
 * bar above, with the card one is in; it leads back to the top. The page keeps one h1.
 */
export function Page({ title, subtitle, actions, back, pills, metrics, children, }) {
    usePageTitle(title);
    const slot = useContext(PageSlot);
    const { ref, gone, card } = useGoneHead();
    const toTop = () => {
        const main = ref.current?.closest('main');
        main?.scrollTo({ top: 0, behavior: 'smooth' });
        main?.focus({ preventScroll: true });
    };
    return (_jsxs("div", { className: "flex min-h-0 flex-1 flex-col gap-6", children: [_jsxs("header", { className: "flex flex-wrap items-start justify-between gap-x-6 gap-y-3 border-b border-hairline pb-4", children: [_jsxs("div", { className: "grid min-w-0 flex-1 basis-[min(100%,18rem)] gap-1.5", children: [back, _jsxs("div", { className: "flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2", children: [_jsx("h1", { ref: ref, className: "min-w-0 text-[1.625rem] leading-[1.1] font-extrabold tracking-[-0.035em] hyphens-auto break-words sm:text-[1.875rem] sm:leading-[1.05]", children: title }), pills] }), subtitle && _jsx("p", { className: "max-w-prose text-sm text-muted", children: subtitle }), metrics && metrics.length > 0 && (_jsx("p", { className: "mt-2 flex flex-wrap items-center gap-x-3.5 gap-y-1 text-[0.8125rem] text-muted", children: metrics.map((metric) => (_jsxs("span", { className: "inline-flex items-center gap-3.5", children: [metric.divider && _jsx("span", { "aria-hidden": true, className: "h-3.5 w-px bg-hairline" }), _jsxs("span", { className: "inline-flex items-baseline gap-1", children: [_jsx("b", { className: cx('font-semibold tabular-nums', metric.tone === 'accent'
                                                        ? 'text-accent-text'
                                                        : metric.tone === 'success'
                                                            ? 'text-success-fg'
                                                            : 'text-text'), children: metric.value }), metric.label] })] }, metric.label))) }))] }), actions && _jsx("div", { className: "flex flex-wrap items-center gap-2", children: actions })] }), children, slot &&
                gone &&
                createPortal(_jsxs(Button, { variant: "quiet", onPress: toTop, "aria-label": `Zum Seitenanfang: ${title}`, className: "min-w-0 motion-safe:animate-[fade-in_150ms_ease-out]", children: [_jsx("span", { className: "truncate text-lg font-semibold tracking-[-0.02em]", children: title }), card && card !== title && (_jsxs(_Fragment, { children: [_jsx("span", { "aria-hidden": true, className: "font-normal text-muted", children: "/" }), _jsx("span", { className: "truncate font-normal text-muted", children: card })] })), _jsx(ArrowUp, { "aria-hidden": true, className: "size-4 shrink-0 text-muted" })] }), slot)] }));
}
/**
 * One area of a section - a page of the settings or of the profile with one card: the page
 * names it, so the card keeps its title for screen readers only (no stutter).
 */
export function AreaPage({ title, subtitle, actions, metrics, children, }) {
    return (_jsx(Page, { title: title, subtitle: subtitle, actions: actions, ...(metrics ? { metrics } : {}), children: _jsx(PageNamesCard.Provider, { value: true, children: children }) }));
}
/**
 * The overview of a section (after Corly): every area as a tile, bundled by topic - the way
 * in for whoever does not know yet where a setting lives. The sidebar holds the same areas.
 * `state` adds what the area stands at, e.g. „3 Sitzungen“ - the reason to look at a tile.
 * `actions` sit beside the title, e.g. a choice of view; `children` replace the list.
 */
export function AreaOverview({ title, groups, link, state, actions, children, }) {
    if (children) {
        return (_jsx(Page, { title: title, actions: actions, children: children }));
    }
    return (_jsx(Page, { title: title, actions: actions, children: groups.map((group) => (_jsxs("section", { "aria-labelledby": `overview-${group.key}`, className: "grid gap-3", children: [_jsx("h2", { id: `overview-${group.key}`, className: "text-lg font-semibold tracking-[-0.02em]", children: group.title }), _jsx("ul", { className: "grid gap-3 sm:grid-cols-2 xl:grid-cols-3", children: group.areas.map(({ slug, title: name, description, icon: Icon }) => (_jsx("li", { children: link(slug, _jsxs(_Fragment, { children: [_jsx("span", { "aria-hidden": true, className: "grid size-10 shrink-0 place-items-center rounded-full bg-accent-soft text-accent-text", children: _jsx(Icon, { className: "size-5" }) }), _jsxs("span", { className: "grid min-w-0 gap-0.5", children: [_jsx("span", { className: "font-semibold", children: name }), _jsx("span", { className: "text-sm text-muted", children: description }), state?.(slug)] })] }), 'glass flex h-full items-start gap-4 rounded-lg p-5 outline-none transition-colors hover:bg-fill-strong focus-visible:outline-2 focus-visible:outline-focus') }, slug))) })] }, group.key))) }));
}
//# sourceMappingURL=area-page.js.map